import React, { Suspense, lazy, useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import NotFound from './NotFound';
import ItemForm from './ItemForm';
import ImageImportModal from './ImageImportModal';
import InventoryImportModal from './InventoryImportModal';
import ItemTypeSelectionModal from './ItemTypeSelectionModal';
import { fieldKey } from '../fieldKeys';
import { fieldErrorMessage } from '../fieldTypes';
import { hasSources, presetUses, usedFields, useKinds } from '../kinds';
import AddFromSourceModal from './AddFromSourceModal';
import AddBookModal from './AddBookModal';
import SourceWarnings from './SourceWarnings';
import { errorMessage } from '../errors';
import { notifyChanged } from '../shell/changes';
import { collectionIcon } from '../shell/collections';
import {
  Alert, Button, Chip, Dialog, EmptyState, ErrorBoundary, IconButton, Menu, PageHeader, Spinner, useConfirm, useToast,
} from '../ui';
import {
  CameraIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, DownloadIcon, EditIcon, ListIcon, LogoutIcon, MoreIcon, PlusIcon,
  ScanIcon, SearchIcon, SettingsIcon, ShareIcon, TrashIcon, UploadIcon,
} from '../ui/icons';
import CollectionToolbar, { sortOptions } from './collection/CollectionToolbar';
import { CollectionDetailsDialog, FieldDefinitionsDialog } from './collection/CollectionDialogs';
import { ItemGrid, ItemList, ItemTable, ResultsSkeleton } from './collection/ItemViews';
import { defaultView, storeView, storedView, typesHaveCovers } from './collection/itemSummary';
import FilterPanel, { fieldsByKeyOf } from './collection/FilterPanel';
import FilterChips from './collection/FilterChips';
import { collectionFields, groupOptions, otherFieldSorts } from './collection/ordering';
import { groupField } from './collection/groups';
import {
  SERIALIZE, emptyFilters, filterApiParams, filterCount, hasFilters, removeFilter,
} from './collection/filters';
import { LOCATION_SORT_KEY, LOCATION_SORT_PARAM, listFromParams, paramsFromList } from './collection/listQuery';
import SavedViewBar from './collection/SavedViewBar';
import { activeView, invalidateSavedViews, refreshSavedViews, useSavedViews } from '../shell/savedViews';
import { pathLabel, useLocations } from '../locations';
import ItemPage from './item/ItemPage';
import { scanPath, scannableTypes } from '../scan/scannable';
import { coverTypes } from '../covers/coverable';
import { canCapture, capturesCovers, opensCapture } from '../capture/capturable';
import { collectionPersonal, isShared } from './item/personal';

// The list is searched, sorted and paged by the server (GET .../items with
// q, sort, dir, page, per_page, type). 60 fills grids of 2, 3, 4, 5 and 6.
export const PER_PAGE = 60;
// The list's address (listFromParams, paramsFromList) lives in collection/listQuery.js.
export { listFromParams, paramsFromList };

// The sub-path after /c/:id — '' for the collection itself, an item's page, or an item form.
// The scanner and its detector are loaded only when it is opened (phase 4 §5.1): the list never pays for them.
const ScanPage = lazy(() => import(/* webpackChunkName: "scan" */ '../scan/ScanPage'));
// The cover pile (phase 4 §5.9): its own chunk, loaded on its page.
const CapturePage = lazy(() => import(/* webpackChunkName: "capture" */ '../capture/CapturePage'));
// CSV imports and exports (phase 4 §6): their own chunk, loaded when a dialog opens.
const ImportWizard = lazy(() => import(/* webpackChunkName: "imports" */ '../imports/ImportWizard'));
const ImportsDialog = lazy(() => import(/* webpackChunkName: "imports" */ '../imports/ImportsDialog'));
const ExportDialog = lazy(() => import(/* webpackChunkName: "imports" */ '../imports/ExportDialog'));

const parseSubPath = (subPath) => {
  const path = (subPath || '').replace(/\/+$/, '');
  if (path === '') return { view: 'list' };
  if (path === 'items/new') return { view: 'new' };
  if (path === 'scan') return { view: 'scan' };
  if (path === 'covers') return { view: 'covers' };
  const page = path.match(/^items\/(\d+)$/);
  if (page) return { view: 'item', itemId: page[1] };
  const edit = path.match(/^items\/(\d+)\/edit$/);
  if (edit) return { view: 'edit', itemId: edit[1] };
  return { view: 'unknown' };
};

// Rendered by the /c/:id/* route, keyed by the collection id: another
// collection is a fresh component. /c/:id/items/:itemId shows the item's page,
// /c/:id/items/new and /c/:id/items/:itemId/edit the item form, in place of
// the list (which is kept, so going back is instant); Back closes them.
function InventoryDetail({ inventory, onUpdate, actions = {} }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const kinds = useKinds();   // which sources fill each preset (GET /kinds)
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const route = parseSubPath(params['*']);
  const basePath = `/c/${inventory.id}`;
  // Item type picked in the form (or in the "which type?" dialog); null = the
  // item's own type, or the collection's only/default type.
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [showImageImport, setShowImageImport] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [showFields, setShowFields] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  // Adding a film or series: TMDB search first, the form when asked ("Enter by hand").
  const [manualEntry, setManualEntry] = useState(false);
  // The item just added from TMDB: highlighted in the list, with a link to it.
  const [justAdded, setJustAdded] = useState(null);   // {id, title, duplicates}
  // The summary of a collection import, shown in a dialog; closing it reloads.
  const [importReport, setImportReport] = useState(null);
  // CSV (phase 4 §6): the import wizard (or an import reopened from the list), the list of imports, the export.
  const [csvImport, setCsvImport] = useState(null);   // null | 'new' | an import from the list
  const [showImports, setShowImports] = useState(false);
  const [showCsvExport, setShowCsvExport] = useState(false);

  const itemTypes = useMemo(() => inventory.item_types || [], [inventory.item_types]);
  const scanTypes = useMemo(() => scannableTypes(inventory), [inventory]);
  const pileTypes = useMemo(() => coverTypes(inventory), [inventory]);
  const Icon = collectionIcon(inventory);

  // The address bar is the truth for the list while it is shown; behind an
  // item form (whose URL has no query) the last list stays as it was.
  const urlListKey = paramsFromList(listFromParams(searchParams, inventory)).toString();
  const [listKey, setListKey] = useState(urlListKey);
  useEffect(() => {
    if (route.view === 'list') setListKey(urlListKey);
  }, [route.view, urlListKey]);
  const list = useMemo(() => listFromParams(new URLSearchParams(listKey), inventory), [listKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // One page of results; the previous page stays on screen while the next loads.
  const [result, setResult] = useState(null); // {items, total, page, per_page, types, listKey}
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const requestCounter = useRef(0);
  const items = result ? result.items : [];
  const loading = result === null && !loadError;

  const canEditItems = inventory.my_permission === 'owner' || inventory.my_permission === 'edit';
  const canManage = inventory.my_permission === 'owner';

  // Grid, list or table: remembered per collection; covers make a grid by
  // default, for readers too — everyone opens items on their page (phase 3).
  const pickedTypes = list.type ? itemTypes.filter(type => type.id === list.type) : itemTypes;
  const hasCovers = typesHaveCovers(itemTypes);
  const [view, setView] = useState(() => storedView(inventory.id) || defaultView(hasCovers));
  // What the types track (my rating, "seen"…), and whether others may rate too.
  const personal = useMemo(() => collectionPersonal(itemTypes), [itemTypes]);
  const shared = isShared(inventory);
  const changeView = (next) => {
    setView(next);
    storeView(inventory.id, next);
  };

  // A saved view opened (phase 3 §5.1, the sidebar's link carries it): its
  // mode applies without replacing the stored choice; what it lost since it
  // was saved (a field removed…) is said. Then the address keeps only which
  // view is open, so a reload or Back/Forward neither applies the mode again
  // over a later choice nor repeats the notice.
  useEffect(() => {
    const state = location.state || {};
    if (!state.savedView || (state.viewMode === undefined && state.dropped === undefined)) return;
    if (['grid', 'list', 'table'].includes(state.viewMode)) setView(state.viewMode);
    if (state.dropped > 0) toast.info(t('views.droppedNotice', { count: state.dropped }));
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: { savedView: state.savedView } });
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // What the server keeps of a view depends on the collection's fields, types
  // and places: when they change, its views are read again.
  const shapeKey = JSON.stringify([inventory.location_type_id || null, inventory.updated_at || null,
    itemTypes.map(type => [type.id, (type.fields || []).map(field => [fieldKey(field), field.type, !!field.unused])])]);
  const firstShape = useRef(shapeKey);
  useEffect(() => {
    if (shapeKey !== firstShape.current) {
      firstShape.current = shapeKey;
      invalidateSavedViews(inventory.id);
    }
  }, [shapeKey, inventory.id]);

  // Changes the list in the address bar, starting from what it says now. Called
  // late (the search box's timer), it goes through a ref, so it never starts
  // from an older URL and undoes a sort or chip clicked in the meantime.
  // The saved view open stays the one open while its list is changed (its "Update").
  const openViewId = location.state && location.state.savedView;
  const openViewRef = useRef(openViewId);
  openViewRef.current = openViewId;
  const recoveredViews = useRef(new Set());
  const updateList = useCallback((changes, { push = false } = {}) => {
    const next = paramsFromList({ ...listFromParams(searchParams, inventory), ...changes });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: !push, state: openViewId ? { savedView: openViewId } : undefined });
    }
  }, [searchParams, setSearchParams, inventory, openViewId]);
  const savedViews = useSavedViews(inventory.id);
  const updateListRef = useRef(updateList);
  updateListRef.current = updateList;
  const search = useCallback((q) => updateListRef.current({ q, page: 1 }), []);

  useEffect(() => {
    const request = ++requestCounter.current;
    setFetching(true);
    api.get(`/inventories/${inventory.id}/items`, {
      params: {
        q: list.q || undefined,
        sort: list.sortKey ? (list.sortKey === LOCATION_SORT_KEY ? LOCATION_SORT_PARAM : list.sortKey) : undefined,
        dir: list.sortKey ? list.direction : undefined,
        group: list.group || undefined,
        page: list.page,
        per_page: PER_PAGE,
        type: list.type || undefined,
        ...filterApiParams(list.filters),
      },
      paramsSerializer: SERIALIZE,
    }).then(response => {
      if (request !== requestCounter.current) return; // an answer to an older request
      setResult({ ...response.data, listKey });
      setLoadError(null);
    }).catch(error => {
      if (request !== requestCounter.current) return;
      // A saved view the list refuses (its collection changed since the views
      // were read): read them again and open what the server keeps of it —
      // never an error screen. Once per view, so it cannot loop.
      const code = error.response?.data?.code;
      const viewId = openViewRef.current;
      if (['invalid_filter', 'invalid_sort', 'invalid_parameter', 'invalid_group'].includes(code) && viewId
          && !recoveredViews.current.has(viewId)) {
        recoveredViews.current.add(viewId);
        refreshSavedViews(inventory.id).then((views) => {
          const fresh = views && views.find(candidate => candidate.id === viewId);
          navigate({ pathname: basePath, search: fresh && fresh.query ? `?${fresh.query}` : '' }, {
            replace: true,
            state: fresh ? { savedView: viewId, dropped: Math.max(1, (fresh.dropped || []).length) } : undefined,
          });
        });
        return;
      }
      // A group the collection no longer has (a field removed, an old link): the list without it.
      if (error.response?.data?.code === 'invalid_group' && list.group) {
        updateListRef.current({ group: null });
        return;
      }
      console.error('Error fetching items:', error);
      setLoadError(errorMessage(t, error));
    }).finally(() => {
      if (request === requestCounter.current) setFetching(false);
    });
  }, [inventory.id, list, reloadToken]); // eslint-disable-line react-hooks/exhaustive-deps
  const refetch = () => setReloadToken(token => token + 1);

  // The filters' counts: their own request (phase 3 §3.4), sent when the
  // search, the type or a filter changes — never for another page or sort.
  const facetQuery = paramsFromList({ ...list, page: 1, sortKey: null, group: null }).toString();
  const facetParams = useMemo(() => ({ q: list.q || undefined, type: list.type || undefined, ...filterApiParams(list.filters) }),
    [facetQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  const [facets, setFacets] = useState(null);
  const [facetsFetching, setFacetsFetching] = useState(false);
  const facetCounter = useRef(0);
  useEffect(() => {
    const request = ++facetCounter.current;
    setFacetsFetching(true);
    api.get(`/inventories/${inventory.id}/facets`, { params: facetParams, paramsSerializer: SERIALIZE })
      .then(response => {
        if (request !== facetCounter.current) return;
        setFacets(response.data && Array.isArray(response.data.facets) ? response.data.facets : []);
      })
      .catch(() => { if (request === facetCounter.current) setFacets(current => current || []); })
      .finally(() => { if (request === facetCounter.current) setFacetsFetching(false); });
  }, [inventory.id, facetParams, reloadToken]);
  const { places } = useLocations(inventory.location_type_id);
  const fieldsByKey = useMemo(() => fieldsByKeyOf(inventory, itemTypes), [inventory, itemTypes]);
  // My rating, "seen" or note changed on an item's page: its card in the list
  // behind shows the answer at once (no note in lists), without reading the page again.
  const patchOpinion = useCallback((itemId, { mine, ratings }) => {
    const listMine = mine ? { rating: mine.rating, done: mine.done, done_on: mine.done_on } : null;
    setResult((current) => (current && current.items.some(item => item.id === itemId)
      ? { ...current, items: current.items.map(item => (item.id === itemId ? { ...item, mine: listMine, ratings } : item)) }
      : current));
  }, []);

  // Past the last page (the last item of a page deleted, or an old link): go to the last one.
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.per_page)) : 1;
  // Only on an answer to the list now shown, once nothing else is loading.
  useEffect(() => {
    if (route.view === 'list' && !fetching && result && result.listKey === listKey
        && result.total > 0 && list.page > pageCount) {
      updateList({ page: pageCount });
    }
  }, [route.view, fetching, result, listKey, list.page, pageCount, updateList]);

  // The scanner's "Enter by hand" (phase 4 §5.6): the form of that type, with the code in its field.
  const prefill = route.view === 'new' && location.state && location.state.prefill ? location.state.prefill : null;
  useEffect(() => {
    if (!prefill) return;
    const type = itemTypes.find(candidate => String(candidate.id) === String(prefill.typeId));
    if (type) {
      setSelectedItemType(type);
      setManualEntry(true);
    }
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving an item form forgets the type chosen for it, and the way of entering it.
  useEffect(() => {
    if (route.view === 'list') {
      setSelectedItemType(null);
      setManualEntry(false);
    }
  }, [route.view]);

  // An item opened by its URL may not be on the current page: fetch it.
  const pageItem = route.view === 'edit' ? items.find(item => String(item.id) === route.itemId) : null;
  const [fetchedItem, setFetchedItem] = useState({ id: null, item: null, missing: false });
  // Forgotten when the form closes: reopened later, the item is read afresh.
  useEffect(() => {
    if (route.view !== 'edit') setFetchedItem(current => (current.id === null ? current : { id: null, item: null, missing: false }));
  }, [route.view]);
  useEffect(() => {
    if (route.view !== 'edit' || pageItem || loading || fetchedItem.id === route.itemId) return;
    const itemId = route.itemId;
    api.get(`/inventories/${inventory.id}/items/${itemId}`)
      .then(response => setFetchedItem({ id: itemId, item: response.data, missing: false }))
      .catch(() => setFetchedItem({ id: itemId, item: null, missing: true }));
  }, [route.view, route.itemId, pageItem, loading, fetchedItem.id, inventory.id]);

  const isFormRoute = route.view === 'new' || route.view === 'edit';
  const editingItem = route.view === 'edit'
    ? pageItem || (fetchedItem.id === route.itemId ? fetchedItem.item : null)
    : null;
  const hasManyTypes = itemTypes.length > 1;
  const itemTypeOf = (item) =>
    (item && item.item_type_id && itemTypes.find(type => type.id === item.item_type_id))
    || inventory.default_item_type;
  const newItemType = hasManyTypes
    ? null
    : (itemTypes.length > 0 ? itemTypes[0] : inventory.default_item_type);
  const formItemType = selectedItemType || (route.view === 'edit' ? itemTypeOf(editingItem) : newItemType);
  // A type whose model has sources is added by searching them first (films and series: TMDB; books: SourceSearch).
  const tmdbFirst = route.view === 'new' && formItemType && hasSources(formItemType.preset, kinds) && !manualEntry;
  const tmdbDialog = tmdbFirst && presetUses(formItemType.preset, 'tmdb', kinds);
  // The form takes the page; the TMDB search and the type choice are dialogs over the list.
  const showFormPage = isFormRoute && formItemType && !tmdbFirst;
  // The form or an item's page in place of the list.
  const listCovered = showFormPage || route.view === 'item' || route.view === 'scan' || route.view === 'covers';

  // The list's scroll position, kept while a form or an item takes the page.
  const listScroll = useRef(0);
  const wasForm = useRef(false);
  useLayoutEffect(() => {
    if (listCovered && !wasForm.current) {
      wasForm.current = true;
      window.scrollTo(0, 0);
    } else if (!listCovered && wasForm.current) {
      wasForm.current = false;
      window.scrollTo(0, listScroll.current);
    }
  }, [listCovered]);
  const rememberScroll = () => { listScroll.current = window.scrollY; };

  // Where the address bar is now, for answers that arrive later.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // Opening a form pushes its URL; closing it goes back when the form was opened
  // from the list (so Back and Close agree), or to the list otherwise.
  const openForm = (path) => {
    rememberScroll();
    navigate(`${basePath}/${path}`, { state: { fromCollection: true } });
  };
  const closeForm = () => {
    // Already elsewhere (left while a save or a deletion was under way): stay there.
    if (!mounted.current || !/\/items\//.test(pathRef.current)) return;
    if (location.state && location.state.fromCollection) {
      navigate(-1);
    } else {
      navigate(basePath, { replace: true });
    }
  };

  // A form the user may not open, or an item that does not exist, is the list.
  const formUnavailable = isFormRoute && !loading
    && (!canEditItems || (route.view === 'edit' && !pageItem
      && fetchedItem.id === route.itemId && fetchedItem.missing));
  useEffect(() => {
    if (formUnavailable) navigate(basePath, { replace: true });
  }, [formUnavailable, basePath, navigate]);

  // After a write the current page is fetched again: the item may have moved
  // in the order, left the search, or a page may have filled up.
  const saveItem = async (itemData) => {
    const formPath = location.pathname;
    try {
      if (editingItem) {
        await api.put(`/inventories/${inventory.id}/items/${editingItem.id}`, itemData);
        setFetchedItem({ id: null, item: null, missing: false });
      } else {
        await api.post(`/inventories/${inventory.id}/items`, itemData);
        notifyChanged('collections');
      }
      refetch();
      if (pathRef.current === formPath) closeForm();
      return true;
    } catch (error) {
      // A refused value is shown on its field by the form.
      if (['invalid_value', 'conversion_running'].includes(error.response?.data?.code)) throw error;
      if (error.response?.data?.code === 'media_taken') {
        toast.error(t('mediaErrors.media_taken'));
        return false;
      }
      console.error('Error saving item:', error);
      toast.error(t('collection.errors.saveItem', { error: errorMessage(t, error) }));
      return false;
    }
  };

  const addedFromSource = ({ item, duplicates, warnings }) => {
    const fields = itemTypes.find(type => type.id === item.item_type_id)?.fields || [];
    setJustAdded({ id: item.id, title: item.field_values?.title || '', duplicates: duplicates || [], warnings: warnings || [], fields });
    notifyChanged('collections');
    refetch();
    closeForm();
  };

  const handleAddItemClick = () => {
    setSelectedItemType(null);
    openForm('items/new');
  };

  // Asks first; true once deleted.
  const deleteItem = async (item) => {
    if (!(await confirm({
      title: t('collection.confirmDeleteItemTitle'),
      confirmLabel: t('collection.deleteItem'),
      danger: true,
    }))) return false;
    try {
      await api.delete(`/inventories/${inventory.id}/items/${item.id}`);
      notifyChanged('collections');
      refetch();
      return true;
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(t('collection.errors.deleteItem', { error: errorMessage(t, error) }));
      return false;
    }
  };

  const saveInventoryDetails = async ({ name, description }) => {
    try {
      const response = await api.put(`/inventories/${inventory.id}`, {
        name,
        description,
        inventory_type_id: inventory.inventory_type_id,
        location_type_id: inventory.location_type_id
      });
      if (onUpdate) onUpdate(response.data);
      setEditingDetails(false);
    } catch (error) {
      console.error('Error updating inventory:', error);
      toast.error(t('collection.errors.update', { error: errorMessage(t, error) }));
    }
  };

  // Items read from a photo name it (source_media_id): the photo stays with them.
  const handleBulkImport = async (extractedItems, sourceMediaId) => {
    try {
      const promises = extractedItems.map(item =>
        api.post(`/inventories/${inventory.id}/items`, sourceMediaId ? { ...item, source_media_id: sourceMediaId } : item)
      );
      const responses = await Promise.all(promises);
      notifyChanged('collections');
      refetch();
      setShowImageImport(false);
      toast.success(t('collection.imageImportDone', { count: responses.length }));
    } catch (error) {
      console.error('Error importing items:', error);
      notifyChanged('collections'); // some may have gone in before the failure
      toast.error(t('collection.errors.importItems', { error: errorMessage(t, error) }));
    }
  };

  const handleExportInventory = async () => {
    try {
      const response = await api.get(`/inventories/${inventory.id}/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // The server's file name, or one made here.
      const contentDisposition = response.headers['content-disposition'];
      let filename = `inventory_${inventory.name.replace(/[^a-zA-Z0-9_-]/g, '_')}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('collection.exportDone'));
    } catch (error) {
      console.error('Error exporting inventory:', error);
      toast.error(t('collection.errors.export', { error: errorMessage(t, error) }));
    }
  };

  // Columns for a table mixing item types: the union of the visible fields of
  // every type, by key — the default type's fields first, in their order, then
  // each other type's extra fields in order. One type picked: its own fields.
  const getVisibleFields = () => {
    const picked = list.type ? itemTypes.find(type => type.id === list.type) : null;
    if (picked) {
      return usedFields(picked.fields).filter(field => field.in_view !== false);
    }
    if (itemTypes.length === 0) {
      return usedFields(inventory.fields).filter(field => field.in_view !== false);
    }
    const defaultType = itemTypes.find(type => type.is_default) || itemTypes[0];
    const orderedTypes = [defaultType, ...itemTypes.filter(type => type !== defaultType)];
    const seen = new Set();
    const fields = [];
    orderedTypes.forEach(type => {
      (type.fields || []).forEach(field => {
        if (field.in_view === false || field.unused || seen.has(fieldKey(field))) return;
        seen.add(fieldKey(field));
        fields.push(field);
      });
    });
    return fields;
  };

  // Column header click: ascending → descending → back to the default order.
  const toggleSort = (key) => {
    let next;
    if (list.sortKey !== key) next = { sortKey: key, direction: 'asc' };
    else if (list.direction === 'asc') next = { sortKey: key, direction: 'desc' };
    else next = { sortKey: null, direction: null };
    updateList({ ...next, page: 1 });
  };

  // The sort select: its fixed choices, "Other field…" (every sortable field
  // of the types, phase 3 §4.2), and the column the table sorts on now.
  const allFields = useMemo(() => collectionFields(inventory, itemTypes), [inventory, itemTypes]);
  const sortValue = list.sortKey ? `${list.sortKey}:${list.direction}` : '';
  const sortChoices = useMemo(() => {
    const choices = [...sortOptions(t, !!inventory.location_type_id, { ...personal, shared }), ...otherFieldSorts(t, allFields)];
    if (sortValue && !choices.some(choice => choice.id === sortValue)) {
      const field = [...(inventory.fields || []), ...itemTypes.flatMap(type => type.fields || [])]
        .find(candidate => fieldKey(candidate) === list.sortKey);
      choices.push({
        id: sortValue,
        label: t(list.direction === 'desc' ? 'collection.sort.fieldDesc' : 'collection.sort.fieldAsc', { name: field ? field.name : list.sortKey }),
      });
    }
    return choices;
  }, [t, inventory, itemTypes, allFields, sortValue, list.sortKey, list.direction, personal, shared]);
  const pickSort = (value) => {
    const [key, direction] = value ? value.split(':') : [null, null];
    updateList({ sortKey: key || null, direction: direction || null, page: 1 });
  };

  // Group by (phase 3 §4.3): the type, the place, the decade, a series, the creators, a coded field.
  const groupChoices = useMemo(() => {
    const choices = groupOptions(t, { fields: allFields, typeCount: itemTypes.length, hasPlaces: !!inventory.location_type_id });
    if (list.group && !choices.some(choice => choice.id === list.group)) {
      const field = groupField(list.group, allFields);
      choices.push({ id: list.group, label: field ? field.name : list.group });
    }
    return choices;
  }, [t, allFields, itemTypes.length, inventory.location_type_id, list.group]);
  const pickGroup = (value) => updateList({ group: value || null, page: 1 });

  const goToPage = (page) => {
    rememberScroll();
    updateList({ page }, { push: true });
    window.scrollTo(0, 0);
  };

  const pickType = (type) => {
    updateList({ type, page: 1 }, { push: true });
  };

  // A filter ticked, typed or removed: a new address, back on page 1 (push for a tick, replace while typing).
  const changeFilters = (filters, { push = true } = {}) => {
    updateListRef.current({ filters, page: 1 }, { push });
  };
  const clearAll = () => updateList({ q: '', type: null, filters: emptyFilters(), page: 1 }, { push: true });

  const handleImportInventory = async (fileContent) => {
    if (!(await confirm({
      title: t('collection.importCollection'),
      message: t('collection.import.confirm'),
      confirmLabel: t('collection.import.confirmButton'),
    }))) {
      return;
    }

    try {
      const body = { type: fileContent.type, data: fileContent };
      let response;
      try {
        response = await api.post(`/inventories/${inventory.id}/import`, body);
      } catch (error) {
        // The members' ratings, "seen" and notes would go with the items: asked apart, then sent again.
        if (error.response?.data?.code !== 'personal_data_would_be_lost') throw error;
        if (!(await confirm({
          title: t('collection.import.personalTitle'),
          message: t('collection.import.personal', { count: error.response.data.count }),
          confirmLabel: t('collection.import.personalConfirm'),
          danger: true,
        }))) return;
        response = await api.post(`/inventories/${inventory.id}/import`, { ...body, discard_personal_data: true });
      }

      const results = response.data.results;
      let message = '';
      message += `• ${t('collection.import.items', { count: results.imported_items })}\n`;
      [
        ['updated_inventory', 'updatedCollection'],
        ['created_inventory_type', 'createdItemType'],
        ['updated_inventory_type', 'updatedItemType'],
        ['created_location_type', 'createdLocationType'],
        ['updated_location_type', 'updatedLocationType'],
      ].forEach(([flag, key]) => {
        if (results[flag]) message += `• ${t(`collection.import.${key}`)}\n`;
      });
      if (results.locations_unsorted > 0) {
        message += `\n${t('collection.import.locationsUnsorted', { count: results.locations_unsorted })}\n`;
      }
      if (results.locations_dropped > 0) {
        message += `\n${t('collection.import.locationsDropped', { count: results.locations_dropped })}\n`;
      }
      if (results.invalid_values > 0) {
        message += `\n${t('collection.import.invalidValues', { count: results.invalid_values })}\n`;
      }
      if (results.images_dropped > 0) {
        message += `\n${t('collection.import.imagesDropped', { count: results.images_dropped })}\n`;
      }
      if (results.errors && results.errors.length > 0) {
        message += `\n${t('collection.import.warnings')}\n${results.errors.slice(0, 3).join('\n')}`;
        if (results.errors.length > 3) {
          message += `\n${t('collection.import.moreWarnings', { count: results.errors.length - 3 })}`;
        }
      }

      // The summary stays until read; closing it reloads the page to show the new data.
      setImportReport(message.trim());
    } catch (error) {
      console.error('Error importing inventory:', error);
      toast.error(t('collection.errors.import', { error: fieldErrorMessage(t, error) || errorMessage(t, error) }));
    }
  };

  if (route.view === 'unknown') {
    return <NotFound />;
  }

  const total = result ? result.total : 0;
  const activeFilters = filterCount(list.filters);
  const filtered = !!(list.q || list.type || activeFilters);
  const collectionSize = !filtered && result ? total : Number(inventory.item_count ?? total);
  const emptyCollection = !!result && total === 0 && !filtered;
  const types = result?.types || [];
  const showTypes = types.length > 1 || !!list.type;
  // Unfiltered, the total counts everything (untyped items of a collection
  // without a default type too); filtered, the sum of the chips is the best known.
  const allCount = list.type ? types.reduce((sum, type) => sum + type.count, 0) : total;
  const number = (n) => new Intl.NumberFormat(i18n.resolvedLanguage || 'en').format(n);

  const menuItems = [
    canManage && { id: 'edit', label: t('collection.editTitle'), icon: EditIcon, onSelect: () => setEditingDetails(true) },
    canManage && actions.onShare && { id: 'share', label: t('collections.share'), icon: ShareIcon, onSelect: () => actions.onShare(inventory) },
    canManage && actions.onEditItemTypes && { id: 'types', label: t('collections.editItemTypes'), icon: SettingsIcon, onSelect: () => actions.onEditItemTypes(inventory) },
    { id: 'fields', label: t('collection.fields.menu'), icon: ListIcon, onSelect: () => setShowFields(true) },
    'separator',
    { id: 'export', label: t('collection.export'), icon: DownloadIcon, onSelect: handleExportInventory },
    { id: 'export-csv', label: t('exportCsv.menu'), icon: DownloadIcon, onSelect: () => setShowCsvExport(true) },
    canEditItems && pileTypes.length > 0 && { id: 'covers', label: t('capture.menu'), icon: CameraIcon, onSelect: () => { rememberScroll(); navigate(scanPath(inventory.id, pileTypes[0].id)); } },
    canEditItems && { id: 'import-csv', label: t('imports.menu'), icon: UploadIcon, onSelect: () => setCsvImport('new') },
    canEditItems && { id: 'imports', label: t('imports.list.menu'), icon: ListIcon, onSelect: () => setShowImports(true) },
    canManage && { id: 'import', label: t('collection.importCollection'), icon: UploadIcon, onSelect: () => setShowImportModal(true) },
    canManage && actions.onDelete && 'separator',
    canManage && actions.onDelete && { id: 'delete', label: t('collections.deleteCollection'), icon: TrashIcon, danger: true, onSelect: () => actions.onDelete(inventory) },
    !inventory.is_mine && !canManage && actions.onLeave && 'separator',
    !inventory.is_mine && !canManage && actions.onLeave && { id: 'leave', label: t('collections.leave'), icon: LogoutIcon, danger: true, onSelect: () => actions.onLeave(inventory) },
  ].filter(Boolean);

  const onCaptureAdded = () => { notifyChanged('collections'); refetch(); };

  const header = (
    <PageHeader
      breadcrumb={[
        { label: inventory.is_mine ? t('shell.myCollections') : t('shell.sharedWithMe'), to: '/' },
        { label: inventory.name },
      ]}
      title={inventory.name}
      subtitle={[
        result || inventory.item_count != null ? t('shell.itemCount', { count: collectionSize }) : null,
        itemTypes.length > 1 ? t('collection.typeCount', { count: itemTypes.length }) : null,
        !inventory.is_mine && inventory.owner_name
          ? (!canEditItems
            ? t('collection.sharedViewOnly', { name: inventory.owner_name })
            : canManage
              ? t('collection.ownedBy', { name: inventory.owner_name })
              : t('collection.sharedCanEdit', { name: inventory.owner_name }))
          : null,
      ].filter(Boolean).join(' · ')}
      actions={(
        <>
          {canEditItems && (
            <Button variant="secondary" icon={CameraIcon} onClick={() => setShowImageImport(true)} className="collection-header__photo"
                    aria-label={t('collection.importFromImage')}>
              {t('collection.importFromImage')}
            </Button>
          )}
          {canCapture(inventory, canEditItems) && (
            <Button as={Link} variant="secondary" icon={ScanIcon} to={scanPath(inventory.id)} onClick={rememberScroll}
                    className="collection-header__scan">
              {opensCapture(inventory, null, canEditItems) ? t('capture.open') : canEditItems ? t('scan.open') : t('scan.openReadOnly')}
            </Button>
          )}
          {canEditItems && (
            <Button icon={PlusIcon} onClick={handleAddItemClick}>{t('collection.addItem')}</Button>
          )}
          <Menu
            label={t('collection.moreActions')}
            align="end"
            items={menuItems}
            trigger={(props) => <IconButton {...props} icon={MoreIcon} label={t('collection.moreActions')} />}
          />
        </>
      )}
    >
      {inventory.description && <p className="collection-description">{inventory.description}</p>}
    </PageHeader>
  );

  const editPath = (item) => `${basePath}/items/${item.id}/edit`;
  // An item's page carries the list it was opened from (not its page): the
  // server places the item in it for ‹ previous / next › (spec §1.1).
  const contextQuery = paramsFromList({ ...list, page: 1 }).toString();
  const itemPath = (item) => `${basePath}/items/${item.id}${contextQuery ? `?${contextQuery}` : ''}`;

  const typeName = list.type ? (types.find(type => type.id === list.type) || itemTypes.find(type => type.id === list.type) || {}).name : null;

  // Saved views (phase 3 §5.1): the one shown, modified when the list or the mode moved away from it.
  const shownView = activeView(savedViews.views, { search: contextQuery, state: location.state }, inventory);
  const viewModified = shownView.modified
    || (!!shownView.view && !!openViewId && !!shownView.view.view && shownView.view.view !== view);
  const viewSummary = [
    list.q && t('views.summary.search', { query: list.q }),
    typeName && t('views.summary.type', { name: typeName }),
    activeFilters > 0 && t('views.summary.filters', { count: activeFilters }),
    list.sortKey && (sortChoices.find(choice => choice.id === sortValue) || {}).label,
    list.group && t('collection.group.by', { name: (groupChoices.find(choice => choice.id === list.group) || { label: list.group }).label }),
    t(`collection.view.${view}`),
  ].filter(Boolean).join(' · ');
  // What the server would refuse to keep (SavedViews::MAX_QUERY, MAX_SEARCH): said before, not after.
  // The query string is percent-encoded ASCII: its length is its size in bytes.
  const viewBlocked = contextQuery.length > 2000 ? t('views.queryTooLong')
    : list.q.length > 200 ? t('views.searchTooLong', { max: 200 }) : null;
  const createView = async (name) => {
    const saved = await savedViews.create({ name, query: contextQuery, view });
    toast.success(t('views.saved', { name: saved.name }));
    // The new view is the one open now.
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: { savedView: saved.id } });
  };
  const updateView = async () => {
    try {
      const saved = await savedViews.update(shownView.view.id, { query: contextQuery, view });
      toast.success(t('views.updated', { name: saved.name }));
    } catch (error) {
      toast.error(t('views.failed', { error: errorMessage(t, error) }));
    }
  };
  const onOpenItem = () => { rememberScroll(); setSelectedItemType(null); };
  const fieldsOf = (item) => (itemTypeOf(item) || {}).fields || inventory.fields || [];
  const highlightId = justAdded ? justAdded.id : null;
  const tableColumns = getVisibleFields();
  const showCoverColumn = pickedTypes.length
    ? pickedTypes.some(type => (type.fields || []).some(field => field.type === 'image'))
    : (inventory.fields || []).some(field => field.type === 'image');

  const renderResults = () => {
    if (loading) return <ResultsSkeleton view={view} />;
    if (!result) return null;
    if (emptyCollection) {
      return (
        <EmptyState
          icon={Icon}
          title={t('collection.empty.title')}
          action={canEditItems && (
            <div className="collection-empty__actions">
              <Button icon={PlusIcon} onClick={handleAddItemClick}>{t('collection.addItem')}</Button>
              <Button variant="secondary" icon={CameraIcon} onClick={() => setShowImageImport(true)}>{t('collection.importFromImage')}</Button>
            </div>
          )}
        >
          {canEditItems ? t('collection.empty.text') : t('collection.empty.readOnly')}
        </EmptyState>
      );
    }
    if (total === 0) {
      if (hasFilters(list.filters)) {
        return (
          <EmptyState
            icon={SearchIcon}
            title={t('collection.noResults')}
            action={<Button variant="secondary" onClick={() => changeFilters(emptyFilters())}>{t('collection.filters.clear')}</Button>}
          >
            {t('collection.filters.noMatch')}
          </EmptyState>
        );
      }
      return list.q ? (
        <EmptyState
          icon={SearchIcon}
          title={t('collection.noResults')}
          action={<Button variant="secondary" onClick={() => updateList({ q: '', page: 1 })}>{t('collection.clearSearch')}</Button>}
        >
          {t('collection.noMatch', { query: list.q })}
        </EmptyState>
      ) : (
        <EmptyState icon={Icon} title={t('collection.emptyGroup')}>
          <Button variant="ghost" onClick={() => pickType(null)}>{t('collection.showAllTypes')}</Button>
        </EmptyState>
      );
    }
    const common = {
      items, canEdit: canEditItems, itemPath, editPath, onOpen: onOpenItem, highlightId, icon: Icon,
      personal, shared,
      group: result.group || null,
      groupInfo: { key: result.group ? result.group.key : null, field: groupField(result.group && result.group.key, allFields), itemTypes, places },
    };
    if (view === 'grid') return <ItemGrid {...common} fieldsOf={fieldsOf} />;
    if (view === 'list') {
      return (
        <ItemList
          {...common}
          fieldsOf={fieldsOf}
          typeName={(item) => (hasManyTypes && !list.type ? (itemTypeOf(item) || {}).name : '')}
          showLocation={!!inventory.location_type_id}
          showCovers={hasCovers}
        />
      );
    }
    return (
      <ItemTable
        {...common}
        columns={tableColumns}
        sort={{ key: list.sortKey, direction: list.direction }}
        onSort={toggleSort}
        showCovers={showCoverColumn}
        showLocation={!!inventory.location_type_id}
        onDelete={deleteItem}
      />
    );
  };

  const renderPager = () => {
    if (!result || pageCount <= 1) return null;
    const page = Math.min(list.page, pageCount);
    const first = (page - 1) * result.per_page + 1;
    const last = Math.min(total, page * result.per_page);
    return (
      <nav aria-label={t('collection.pager.label')} className="collection-pager">
        <span className="collection-pager__range">{t('collection.pager.range', { first: number(first), last: number(last), total: number(total) })}</span>
        <span className="collection-pager__buttons">
          <Button variant="secondary" size="sm" icon={ChevronLeftIcon} onClick={() => goToPage(page - 1)} disabled={page <= 1}>
            {t('collection.pager.previous')}
          </Button>
          <span className="collection-pager__page">{t('collection.pager.pageOf', { page, pages: pageCount })}</span>
          <Button variant="secondary" size="sm" iconRight={ChevronRightIcon} onClick={() => goToPage(page + 1)} disabled={page >= pageCount}>
            {t('collection.pager.next')}
          </Button>
        </span>
      </nav>
    );
  };


  // The panel beside the results on a desktop, in the "Sort and filter" sheet below 1024 px.
  const showPanel = !emptyCollection && !loading;
  const panel = (inSheet) => (
    <FilterPanel
      facets={facets}
      loading={facetsFetching}
      filters={list.filters}
      onChange={changeFilters}
      fieldsByKey={fieldsByKey}
      personal={personal}
      inventoryId={inventory.id}
      listParams={facetParams}
      places={places}
      className={inSheet ? 'filter-panel--sheet' : undefined}
    />
  );

  return (
    <div className="collection-page">
      {showFormPage && !formUnavailable && (route.view !== 'edit' || editingItem) && (
        <ItemForm
          key={route.view === 'edit' ? `edit-${route.itemId}` : 'new'}
          inventory={inventory}
          item={editingItem}
          selectedItemType={formItemType}
          availableItemTypes={inventory.item_types}
          onSave={saveItem}
          onClose={closeForm}
          onDelete={editingItem ? async () => { const formPath = location.pathname; if (await deleteItem(editingItem) && pathRef.current === formPath) closeForm(); } : undefined}
          onItemTypeChange={setSelectedItemType}
          onRefreshed={() => refetch()}
          initialValues={prefill && formItemType && String(formItemType.id) === String(prefill.typeId) ? prefill.values : undefined}
        />
      )}
      {showFormPage && route.view === 'edit' && !editingItem && !formUnavailable && <ResultsSkeleton view="list" />}

      {/* /scan and /covers: one capture screen for films, series and books (§5.10), the scanner for the rest. */}
      {(route.view === 'scan' || route.view === 'covers') && (
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={24} label={t('common.loading')} />}>
            {/* A pile carried on by its address alone (?batch=, no type): the capture screen, which takes the batch's type. */}
            {opensCapture(inventory, searchParams.get('type'), canEditItems) || (canEditItems && !searchParams.get('type') && searchParams.get('batch') && coverTypes(inventory).length > 0) ? (
              <CapturePage inventory={inventory} canEdit={canEditItems} basePath={basePath} onAdded={onCaptureAdded} />
            ) : (
              <ScanPage inventory={inventory} canEdit={canEditItems} basePath={basePath} onAdded={onCaptureAdded} />
            )}
          </Suspense>
        </ErrorBoundary>
      )}

      {route.view === 'item' && (
        <ItemPage
          key={route.itemId}
          inventory={inventory}
          itemId={route.itemId}
          itemTypes={itemTypes}
          icon={Icon}
          canEdit={canEditItems}
          basePath={basePath}
          context={paramsFromList({ ...listFromParams(searchParams, inventory), page: 1 })}
          perPage={PER_PAGE}
          onDelete={deleteItem}
          onChanged={refetch}
          onOpinionSaved={patchOpinion}
        />
      )}

      <div hidden={listCovered} className="collection-page__list">
        {header}

        {!emptyCollection && (
          <CollectionToolbar
            inventory={inventory}
            query={list.q}
            onSearch={search}
            sortValue={sortValue}
            sortChoices={sortChoices}
            onSort={pickSort}
            view={view}
            onView={changeView}
            types={types}
            allCount={allCount}
            type={list.type}
            onType={pickType}
            showTypes={showTypes}
            filterCount={activeFilters}
            total={result ? total : null}
            filterPanel={panel(true)}
            groupValue={list.group || ''}
            groupChoices={groupChoices}
            onGroup={pickGroup}
          />
        )}

        <div className={`collection-body${showPanel ? ' has-panel' : ''}`}>
        {showPanel && (
          <aside className="collection-panel" aria-labelledby="collection-panel-title">
            <h2 id="collection-panel-title" className="collection-panel__title">{t('collection.filters.label')}</h2>
            {panel(false)}
          </aside>
        )}
        <div className="collection-main">
        {result && !emptyCollection && (
          <SavedViewBar
            inventoryId={inventory.id}
            active={{ view: shownView.view, modified: viewModified }}
            canSave={contextQuery !== ''}
            summary={viewSummary}
            onCreate={createView}
            onUpdate={updateView}
            blocked={viewBlocked}
          />
        )}
        <div className="collection-status">
          {filtered && result && (
            <div className="collection-filters">
              <span className="collection-filters__count">
                {t('collection.matchCount', { shown: number(total), of: number(Number(inventory.item_count ?? total)) })}
              </span>
              {list.q && <Chip label={t('collection.searchChip')} value={list.q} onRemove={() => updateList({ q: '', page: 1 })} />}
              {list.type && <Chip label={t('collection.typeChip')} value={typeName || '…'} onRemove={() => pickType(null)} />}
              <FilterChips filters={list.filters} facets={facets} fieldsByKey={fieldsByKey} places={places} personal={personal}
                           onRemove={(chip) => changeFilters(removeFilter(list.filters, chip))} />
              <Button variant="ghost" size="sm" onClick={clearAll}>{t('collection.clearAll')}</Button>
            </div>
          )}
          <span className="visually-hidden" aria-live="polite">
            {fetching ? t('collection.updating') : result ? t('collection.results', { count: total }) : ''}
          </span>
        </div>

        {justAdded && (
          <Alert
            tone="success"
            className="collection-added"
            action={(
              <>
                <Button as={Link} variant="ghost" size="sm" to={itemPath(justAdded)} state={{ fromCollection: true }} onClick={rememberScroll}>{t('addFromSource.open')}</Button>
                <IconButton icon={CloseIcon} variant="ghost" size="sm" label={t('addFromSource.dismiss')} onClick={() => setJustAdded(null)} className="collection-added__close" />
              </>
            )}
          >
            <p>{t('addFromSource.added', { title: justAdded.title })}</p>
            {justAdded.duplicates.length > 0 && <p className="collection-added__duplicate">{t('addFromSource.addedDuplicate', { count: justAdded.duplicates.length })}</p>}
            {justAdded.warnings.length > 0 && <SourceWarnings warnings={justAdded.warnings} fields={justAdded.fields} />}
          </Alert>
        )}

        {loadError && <Alert tone="danger">{t('collection.errors.load', { error: loadError })}</Alert>}

        <section aria-label={t('collection.itemsHeading')} aria-busy={fetching || loading}
                 className={`collection-results${fetching && result ? ' is-fetching' : ''}`}>
          {renderResults()}
        </section>
        {renderPager()}
        {list.q && result && (
          <p className="collection-everywhere">
            <Link to={`/search?q=${encodeURIComponent(list.q)}`}>{t('collection.searchEverywhere', { query: list.q })}</Link>
          </p>
        )}
        </div>
        </div>
      </div>

      {route.view === 'new' && !formUnavailable && tmdbFirst && tmdbDialog && (
        <AddFromSourceModal
          inventory={inventory}
          itemType={formItemType}
          onAdded={addedFromSource}
          onManual={() => setManualEntry(true)}
          onScan={scanTypes.some(type => type.id === formItemType.id) || capturesCovers(formItemType) ? () => navigate(scanPath(inventory.id, formItemType.id), { replace: true }) : undefined}
          scanLabel={capturesCovers(formItemType) ? t('capture.open') : undefined}
          onClose={closeForm}
        />
      )}
      {route.view === 'new' && !formUnavailable && tmdbFirst && !tmdbDialog && (
        <AddBookModal
          inventory={inventory}
          itemType={formItemType}
          onAdded={addedFromSource}
          onManual={() => setManualEntry(true)}
          onPhoto={() => { closeForm(); setShowImageImport(true); }}
          onScan={scanTypes.some(type => type.id === formItemType.id) || capturesCovers(formItemType) ? () => navigate(scanPath(inventory.id, formItemType.id), { replace: true }) : undefined}
          scanLabel={capturesCovers(formItemType) ? t('capture.open') : undefined}
          onClose={closeForm}
        />
      )}

      {showImageImport && (
        <ImageImportModal
          inventory={inventory}
          onImport={handleBulkImport}
          onClose={() => setShowImageImport(false)}
        />
      )}

      <Suspense fallback={null}>
      {csvImport && (
        <ImportWizard inventory={inventory} initialImport={csvImport === 'new' ? null : csvImport}
          places={places.filter(place => !(place.path && place.path[0] && place.path[0].is_unsorted)).map(place => ({ id: place.id, label: pathLabel(t, place.path) }))}
          onClose={() => setCsvImport(null)} onDone={() => { notifyChanged('collections'); refetch(); }} />
      )}
      {showImports && (
        <ImportsDialog inventory={inventory} onClose={() => setShowImports(false)}
          onOpen={(entry) => { setShowImports(false); setCsvImport(entry); }} />
      )}
      {showCsvExport && (
        <ExportDialog inventory={inventory} listParams={new URLSearchParams(listKey)} viewCount={result ? result.total : null}
          totalCount={Number(inventory.item_count ?? (result ? result.total : 0))} onClose={() => setShowCsvExport(false)} />
      )}
      </Suspense>

      {showImportModal && (
        <InventoryImportModal
          onImport={handleImportInventory}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {route.view === 'new' && !formUnavailable && !formItemType && (
        <ItemTypeSelectionModal
          inventory={inventory}
          onSelectItemType={setSelectedItemType}
          onClose={closeForm}
        />
      )}

      {editingDetails && (
        <CollectionDetailsDialog inventory={inventory} onSave={saveInventoryDetails} onClose={() => setEditingDetails(false)} />
      )}
      {showFields && (
        <FieldDefinitionsDialog inventory={inventory} canManage={canManage} onClose={() => setShowFields(false)} />
      )}

      <Dialog
        open={!!importReport}
        onClose={() => window.location.reload()}
        title={t('collection.import.done')}
        description={importReport}
        footer={<Button onClick={() => window.location.reload()}>{t('collection.import.showResult')}</Button>}
      />
    </div>
  );
}

export default InventoryDetail;
